import React from "react";
import styled from "styled-components";
// import dayjs from "dayjs";
import { getLocale } from "react-i18nify";

import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import {
  Black,
  DarkGrey,
  GreyStroke,
  Primary,
  White,
  White01,
} from "src/styles/color";

import LabelSection from "src/components/core/LabelSection";
import Chip from "src/components/core/Chip";

export const StyledSelect = styled(Select)`
  border-radius: 16px;
  background-color: ${White};
  text-align: center;

  .MuiSelect-select {
    padding: 8px 16px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }

  @media ${device.tabletL} {
    .MuiSelect-select {
      padding: 8px 12px;
    }
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    line-height: 24px;
    min-height: 40px;
    padding: 8px 24px;
  }

  @media ${device.tabletL} {
    &.MuiMenuItem-root {
      padding: 8px 16px;
    }
  }
`;

const TopViewsBox = styled.div`
  display: flex;
  /* align-items: center; */
  flex-wrap: wrap;
  border: 1px solid ${GreyStroke};
  border-radius: 16px;

  /* &:hover {
    background-color: ${GreyStroke};
  } */
`;

const TopRankViewsImg = styled.div`
  width: 100%;
  max-width: 180px;
  height: 100%;
  border-radius: inherit;
  background-image: ${({ src }) => `url(${src})`};
  background-size: cover;
  background-position: center;

  @media ${device.mobileL} {
    max-width: 150px;
  }
`;

const TopRankViewsInfo = styled.div`
  /* width: calc(100% - 180px); */
  width: max(60%, calc(100% - 180px));
  height: 100%;

  @media ${device.mobileL} {
    width: max(60%, calc(100% - 150px));
  }
`;

const TopRankViewsDetail = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid ${GreyStroke};
`;

const TopViewsNumber = styled.div`
  font-size: ${({ fontSize }) => fontSize || font.size.xl};
  font-weight: 600;
  color: ${({ color }) => color || Primary};
  text-align: ${({ center }) => (center ? "center" : "left")};
  padding: ${({ center }) => (center ? "0 8px" : "0")};

  span {
    font-size: ${font.size.xs};
    font-weight: 400;
    color: ${DarkGrey};
  }

  @media ${device.tabletL} {
    font-size: ${({ fontSize }) => fontSize || font.size.lg};
  }
`;

const TopViewsTableHeader = styled.div`
  width: 100%;
  display: flex;
  padding: 12px 16px;
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid ${GreyStroke};
  background-color: ${White01};
`;

const TopViewsTableRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${GreyStroke};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 16px 16px;
  }

  &:nth-child(odd) {
    background-color: #fafafa;
  }
`;

const TopViewsTableItem = styled.div`
  flex: ${({ flex }) => flex || "1"};
`;

const TopViewsTable = ({ data = [], selectedTimeRangeData }) => {
  // console.log("---data---", data);
  return (
    <LabelSection
      title={
        getLocale() === "th" ? "Top SKU ยอดวิวสูงสุด" : "Top SKU Daily Views"
      }
      description={
        <Typography
          variant="body2"
          sx={{
            ml: 2.5,
            "&.MuiTypography-root": { fontSize: font.size.xs },
          }}
        >
          {getLocale() === "th"
            ? `อิงจากยอดวิวสินค้า ${selectedTimeRangeData?.label?.th}`
            : `Based on product views ${selectedTimeRangeData?.label?.en}`}
        </Typography>
      }
      style={{ marginBottom: 0, height: "100%" }}
      titleStyle={{ fontSize: font.size.lg, fontWeight: 600 }}
      headerStyle={{ marginBottom: 16 }}
    >
      <Grid container spacing={2} sx={{ flex: 1 }}>
        {data?.length !== 0 ? (
          <>
            {/* Top views rank */}
            <Grid item xs={12}>
              <TopViewsBox>
                <Box
                  sx={{
                    width: "40%",
                    maxWidth: { xs: 150, sm: 180 },
                    backgroundColor: White01,
                    borderRadius: {
                      xs: "16px 0 0 0",
                      sm: "16px 0 0 16px",
                    },
                    borderRight: `1px solid ${GreyStroke}`,
                  }}
                >
                  {checkNotEmpty(data?.[0]?.productFullImage) ? (
                    <TopRankViewsImg src={data?.[0]?.productFullImage} />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 60, color: GreyStroke }} />
                    </Box>
                  )}
                </Box>

                <TopRankViewsInfo>
                  <TopRankViewsDetail>
                    <Chip
                      label="Rank #1"
                      variant="secondary"
                      color="warning"
                      // size="small"
                      sx={{ width: "fit-content", borderRadius: "8px" }}
                    />

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.xs,
                          },
                        }}
                      >
                        {data?.[0]?.itemId}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          "&.MuiTypography-root": { fontWeight: 600 },
                        }}
                      >
                        {data?.[0]?.itemName}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.sm,
                            fontWeight: 400,
                          },
                        }}
                      >
                        {/* {`${data?.[0]?.color} | ${data?.[0]?.itemCategory}`} */}
                        {`${data?.[0]?.itemBrand}`}
                      </Typography>
                    </Box>

                    {/* <Stack direction="row" alignItems="center" gap={1}>
                      <Box sx={{ maxWidth: "48%", flex: "1 1 auto" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            "&.MuiTypography-root": {
                              fontSize: font.size.xs,
                              color: DarkGrey,
                              lineHeight: 0.8,
                            },
                          }}
                        >
                          ลาย:
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            "&.MuiTypography-root": {
                              fontSize: font.size.sm,
                              fontWeight: 500,
                            },
                          }}
                        >
                          {data?.[0]?.pattern}
                        </Typography>
                      </Box>

                      <Box sx={{ maxWidth: "48%", flex: "1 1 auto" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            "&.MuiTypography-root": {
                              fontSize: font.size.xs,
                              color: DarkGrey,
                              lineHeight: 0.8,
                            },
                          }}
                        >
                          ผิว:
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            "&.MuiTypography-root": {
                              fontSize: font.size.sm,
                              fontWeight: 500,
                            },
                          }}
                        >
                          {data?.[0]?.surface}
                        </Typography>
                      </Box>
                    </Stack> */}
                  </TopRankViewsDetail>

                  {/* desktop/tablet display */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={2}
                    padding={2}
                    sx={{ display: { xs: "none", sm: "flex" } }}
                  >
                    <Box sx={{ flex: "1 1 45%" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.xs,
                            lineHeight: 0.8,
                          },
                        }}
                      >
                        ยอดเข้าชม
                      </Typography>
                      <TopViewsNumber>
                        {convertIntToCurrency(data?.[0]?.itemsViewed, false)}{" "}
                        <span>{getLocale() === "th" ? `ครั้ง` : `times`}</span>
                      </TopViewsNumber>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ borderColor: GreyStroke }}
                    />

                    <Box sx={{ flex: "1 1 45%" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.xs,
                            lineHeight: 0.8,
                          },
                        }}
                      >
                        ยอดเข้าชมสะสม
                      </Typography>
                      <TopViewsNumber>
                        {convertIntToCurrency(
                          data?.[0]?.productViewCount,
                          false,
                        )}{" "}
                        <span>{getLocale() === "th" ? `ครั้ง` : `times`}</span>
                      </TopViewsNumber>
                    </Box>
                  </Stack>
                </TopRankViewsInfo>

                {/* mobile display */}
                <Stack
                  direction="row"
                  alignItems="center"
                  // justifyContent="space-between"
                  gap={2}
                  padding={2}
                  sx={{
                    width: "100%",
                    display: { xs: "flex", sm: "none" },
                  }}
                >
                  <Box sx={{ flex: "1 1 45%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        "&.MuiTypography-root": {
                          fontSize: font.size.xs,
                          lineHeight: 0.8,
                        },
                      }}
                    >
                      ยอดเข้าชม
                    </Typography>
                    <TopViewsNumber>
                      {convertIntToCurrency(data?.[0]?.itemsViewed, false)}{" "}
                      <span>{getLocale() === "th" ? `ครั้ง` : `times`}</span>
                    </TopViewsNumber>
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: DarkGrey }}
                  />

                  <Box sx={{ flex: "1 1 45%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        "&.MuiTypography-root": {
                          fontSize: font.size.xs,
                          lineHeight: 0.8,
                        },
                      }}
                    >
                      ยอดเข้าชมสะสม
                    </Typography>
                    <TopViewsNumber>
                      {convertIntToCurrency(data?.[0]?.productViewCount, false)}{" "}
                      <span>{getLocale() === "th" ? `ครั้ง` : `times`}</span>
                    </TopViewsNumber>
                  </Box>
                </Stack>
              </TopViewsBox>
            </Grid>

            {/* Top views table (rank 2-5) */}
            {checkNotEmpty(data?.slice(1, 5)) ? (
              <Grid item xs={12}>
                <TopViewsBox>
                  {/* header */}
                  <TopViewsTableHeader>
                    <TopViewsTableItem flex={1}></TopViewsTableItem>

                    <TopViewsTableItem flex={4}>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.sm,
                            fontWeight: 500,
                          },
                        }}
                      >
                        สินค้า
                      </Typography>
                    </TopViewsTableItem>

                    {/* <TopViewsTableItem flex={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        "&.MuiTypography-root": {
                          fontSize: font.size.sm,
                          fontWeight: 500,
                          textAlign: "center",
                        },
                      }}
                    >
                      ราคา/หน่วย
                    </Typography>
                  </TopViewsTableItem> */}

                    <TopViewsTableItem flex={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.sm,
                            fontWeight: 500,
                            textAlign: "center",
                          },
                        }}
                      >
                        ยอดเข้าชม
                      </Typography>
                    </TopViewsTableItem>

                    <TopViewsTableItem flex={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          "&.MuiTypography-root": {
                            fontSize: font.size.sm,
                            fontWeight: 500,
                            textAlign: "center",
                          },
                        }}
                      >
                        ยอดเข้าชมสะสม
                      </Typography>
                    </TopViewsTableItem>
                  </TopViewsTableHeader>

                  {/* rows */}
                  {data?.slice(1, 5).map((item, index) => (
                    <TopViewsTableRow key={index}>
                      <TopViewsTableItem flex={1}>
                        <Chip
                          label={index + 2}
                          variant="filled"
                          // color="warning"
                          size="small"
                          sx={{
                            width: "fit-content",
                            borderRadius: "8px",
                            mr: 1,
                          }}
                        />
                      </TopViewsTableItem>

                      <TopViewsTableItem flex={4}>
                        <Typography
                          variant="body1"
                          sx={{
                            "&.MuiTypography-root": {
                              lineHeight: 1.2,
                              fontWeight: 500,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            },
                          }}
                        >
                          {item?.itemName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            "&.MuiTypography-root": {
                              fontSize: font.size.xs,
                            },
                          }}
                        >
                          {item?.itemId} | {item?.itemBrand}
                        </Typography>
                      </TopViewsTableItem>

                      {/* <TopViewsTableItem flex={2}>
                      <TopViewsNumber fontSize={font.size.md} center>
                        {convertIntToCurrency(item?.group_price?.price, false)}{" "}
                        <span>
                          /
                          {getLocale() === "th"
                            ? item?.group_price?.unit_price?.unit_th
                            : item?.group_price?.unit_price?.unit_en}
                        </span>
                      </TopViewsNumber>
                    </TopViewsTableItem> */}

                      <TopViewsTableItem flex={2}>
                        <TopViewsNumber
                          color={Black}
                          fontSize={font.size.md}
                          center
                        >
                          {convertIntToCurrency(item?.itemsViewed, false)}{" "}
                        </TopViewsNumber>
                      </TopViewsTableItem>

                      <TopViewsTableItem flex={2}>
                        <TopViewsNumber
                          color={Black}
                          fontSize={font.size.md}
                          center
                        >
                          {convertIntToCurrency(item?.productViewCount, false)}{" "}
                        </TopViewsNumber>
                      </TopViewsTableItem>
                    </TopViewsTableRow>
                  ))}
                </TopViewsBox>
              </Grid>
            ) : null}
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  "&.MuiTypography-root": {
                    fontSize: font.size.sm,
                    color: DarkGrey,
                  },
                }}
              >
                {getLocale() === "th" ? "ไม่มีข้อมูล" : "No data available"}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </LabelSection>
  );
};

export default TopViewsTable;
